import Papa from 'papaparse';
import type { Sprint } from '@/types/sprint';

interface SprintCSVRow {
  sprint_number?: string;
  sprint_name?: string;
  name?: string;
  start_date?: string;
  start?: string;
  end_date?: string;
  end?: string;
  [key: string]: string | undefined;
}

export interface ParsedSprintRow {
  sprint_number: number;
  name: string;
  start_date: Date;
  end_date: Date;
}

/**
 * Parses a date string supporting YYYY-MM-DD, MM/DD/YYYY, and DD-MM-YYYY formats.
 * DD-MM-YYYY is only accepted when day > 12 to avoid silent ambiguity with MM-DD-YYYY.
 * Returns null when the string is missing, empty, does not match any format,
 * or the parsed date rolls over to a different calendar date (e.g., Feb 30).
 */
export function parseDate(dateString: string | undefined): Date | null {
  if (!dateString || dateString.trim() === '') return null;

  const s = dateString.trim();

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    const [, m, day] = s.split('-');
    if (d.getMonth() + 1 !== parseInt(m!, 10) || d.getDate() !== parseInt(day!, 10)) return null;
    return d;
  }

  // MM/DD/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
    const [month, day, year] = s.split('/');
    const d = new Date(`${year}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    if (d.getMonth() + 1 !== parseInt(month!, 10) || d.getDate() !== parseInt(day!, 10)) return null;
    return d;
  }

  // DD-MM-YYYY — only accepted when day > 12 to avoid ambiguity with MM-DD-YYYY
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(s)) {
    const [day, month, year] = s.split('-');
    const dayNum = parseInt(day!, 10);
    if (dayNum <= 12) return null;
    const d = new Date(`${year}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    if (d.getMonth() + 1 !== parseInt(month!, 10) || d.getDate() !== dayNum) return null;
    return d;
  }

  return null;
}

/**
 * Parses a CSV File into a ParsedSprintRow[] array.
 * Accepts flexible column names: sprint_name/name, start_date/start, end_date/end.
 * Validates that end_date > start_date, sprint numbers are positive integers,
 * sprint names are ≤ 100 characters, and no duplicate sprint numbers exist in the batch.
 * Throws with a descriptive message on the first validation failure.
 */
export function parseSprintCSV(file: File): Promise<ParsedSprintRow[]> {
  return new Promise((resolve, reject) => {
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      reject(new Error('Please upload a CSV file'));
      return;
    }

    Papa.parse<SprintCSVRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Surface fatal parse errors (malformed quoting, wrong delimiter)
        const fatalError = results.errors.find((e) => e.type !== 'FieldMismatch');
        if (fatalError) {
          reject(new Error(`CSV parsing error: ${fatalError.message}`));
          return;
        }

        if (results.data.length === 0) {
          reject(new Error('CSV file is empty'));
          return;
        }

        const firstRow = results.data[0]!;
        // Strip UTF-8 BOM from first key (common in Excel exports)
        const keys = Object.keys(firstRow).map((k) =>
          k.replace(/^\uFEFF/, '').toLowerCase().trim()
        );

        const hasStart = keys.some((k) => k === 'start_date' || k === 'start');
        const hasEnd = keys.some((k) => k === 'end_date' || k === 'end');

        if (!hasStart || !hasEnd) {
          reject(new Error('CSV must include: sprint_name, start_date, end_date'));
          return;
        }

        try {
          const sprints: ParsedSprintRow[] = results.data.map((row, index) => {
            const rowNum = index + 1;

            // Normalise keys for case-insensitive lookup; strip BOM from first key
            const normalised: Record<string, string | undefined> = {};
            for (const [k, v] of Object.entries(row)) {
              normalised[k.replace(/^\uFEFF/, '').toLowerCase().trim()] = v;
            }

            const rawName = normalised['sprint_name'] ?? normalised['name'];
            const rawStart = normalised['start_date'] ?? normalised['start'];
            const rawEnd = normalised['end_date'] ?? normalised['end'];
            const rawNumber = normalised['sprint_number'];

            const sprintName = rawName?.trim() || `Sprint ${index + 1}`;
            if (sprintName.length > 100) {
              throw new Error(`Row ${rowNum}: sprint name exceeds 100 characters`);
            }

            const sprintNumber = rawNumber ? Number(rawNumber) : index + 1;
            if (rawNumber && (!Number.isInteger(sprintNumber) || sprintNumber <= 0)) {
              throw new Error(`Row ${rowNum} has invalid sprint number`);
            }

            const startDate = parseDate(rawStart);
            const endDate = parseDate(rawEnd);

            if (!startDate) {
              throw new Error(`Row ${rowNum} has invalid date format`);
            }
            if (!endDate) {
              throw new Error(`Row ${rowNum} has invalid date format`);
            }
            if (endDate <= startDate) {
              throw new Error(`Row ${rowNum}: end date must be after start date`);
            }

            return { sprint_number: sprintNumber, name: sprintName, start_date: startDate, end_date: endDate };
          });

          // Validate no duplicate sprint numbers within the batch
          const seen = new Set<number>();
          for (const s of sprints) {
            if (seen.has(s.sprint_number)) {
              throw new Error(`Duplicate sprint number ${s.sprint_number} in CSV`);
            }
            seen.add(s.sprint_number);
          }

          resolve(sprints);
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      },
      error: (error) => {
        reject(new Error(`CSV parsing error: ${error.message}`));
      },
    });
  });
}

/** Returns the inclusive duration in days between two sprint dates. */
export function sprintDurationDays(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

// Re-export ParsedSprintRow as compatible with the Sprint shape (minus id/user_id)
export type { Sprint };
